/**
 * PerformanceTab - CR-6310
 *
 * Performance Scorecard summary matching the legacy SupplierPortal Dashboard.
 * Displays aggregated YTD and Month metrics in a colored-section layout:
 * - Line Orders (blue)
 * - Delivery Performance (green)
 * - Cost (orange)
 * - Returns (red)
 * - Quality (gray)
 * - Supplier Concerns (teal)
 * - Supplier Rating cards with A-F grades
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { exportPerformancePDF } from './performancePdfExport'
import { getDefaultDateRange, type SupplierOption } from './performanceConstants'
import { PerformanceFilterBar } from './PerformanceFilterBar'
import { PerformanceScorecardTable, type SectionDataMap, type DrilldownFilters } from './PerformanceScorecardTable'
import { PerformanceRatingCards } from './PerformanceRatingCards'
import { PerformanceDailyOTIFChart } from './PerformanceDailyOTIFChart'
import { PerformanceChangeRequestsCharts } from './PerformanceChangeRequestsCharts'
import { Card, CardContent } from '../ui/card'
import { TabProps } from './types'
import { useFormatters } from '../../contexts/PreferencesContext'
import { useViewKey } from '../../contexts/ViewContext'
import { RefreshCw, BarChart3, Loader2, Filter, FileDown } from 'lucide-react'
import { Button } from '../ui/button'
import { useSupplierCustomer } from './useSupplierCustomer'
import { showsOtif } from './deliveryMetricMode'
import { performanceScorecardAPI, coreAPI, type PerformanceScorecardData } from '../../lib/api'

interface PerformanceTabProps extends TabProps {
  /** Supplier portal, as Dashboard resolves it. Comes from the caller rather
   *  than usePortalMode so an admin using the supplier menu-type override
   *  gets the supplier view of a tab the same override made visible. */
  isSupplier?: boolean
}

export function PerformanceTab({ data, filters: _filters, isSupplier: portalIsSupplier = false }: PerformanceTabProps) {
  const { formatNumber, formatCurrency, preferences } = useFormatters()
  // CR-8697: the customer's Delivery Metric Mode hides the OTIF card and chart
  // when it switches OTIF off. The supplier portal's view is unchanged.
  const showOTIF = portalIsSupplier || showsOtif(data?.delivery_metric_mode)
  const [scorecard, setScorecard] = useState<PerformanceScorecardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showFilters, setShowFilters] = useState(true)
  // Benign "nothing to report against yet" state, kept apart from `error` so a
  // supplier with no trading relationship gets an empty state, not a failure.
  const [noCustomer, setNoCustomer] = useState(false)

  // Supplier portal: the endpoint is keyed on the customer, not the supplier.
  const {
    customerId: supplierCustomerId,
    customers,
    setCustomerId: setSupplierCustomerId,
    loading: loadingCustomers,
  } = useSupplierCustomer(portalIsSupplier)

  // Supplier list for dropdown
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([])
  const [loadingSuppliers, setLoadingSuppliers] = useState(false)

  // Filter state
  const defaultDates = getDefaultDateRange()
  const [selectedSupplier, setSelectedSupplier] = useState<string>('all')
  const [startDate, setStartDate] = useState(defaultDates.startDate)
  const [endDate, setEndDate] = useState(defaultDates.endDate)
  const [strategicOnly, setStrategicOnly] = useState(false)

  // Applied filters (only update on Search click)
  const [appliedFilters, setAppliedFilters] = useState({
    supplier: 'all',
    startDate: defaultDates.startDate,
    endDate: defaultDates.endDate,
    strategicOnly: false,
  })

  // PDF export state and refs
  const [exporting, setExporting] = useState(false)
  const scorecardRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<HTMLDivElement>(null)

  // Load suppliers for dropdown — customer side only. A supplier user reports
  // on itself (the backend pins supplier_id to their own company), so there is
  // nothing to choose and no call worth making.
  const viewKey = useViewKey()  // supplier options follow the site switcher
  useEffect(() => {
    if (portalIsSupplier) return
    const loadSuppliers = async () => {
      setLoadingSuppliers(true)
      try {
        const params = strategicOnly ? { strategic_only: true } : undefined
        const response = await coreAPI.getSupplierSitesForFilter(params)
        const list = (response.data.results || [])
          .filter((s) => s.id && s.name)
          .map((s) => ({ id: s.id, name: s.name }))
        setSuppliers(list)
        // Reset selection if supplier no longer in filtered list
        if (selectedSupplier !== 'all' && !list.some((s) => String(s.id) === selectedSupplier)) {
          setSelectedSupplier('all')
        }
      } catch (err) {
        console.error('[PerformanceTab] Error loading suppliers:', err)
      } finally {
        setLoadingSuppliers(false)
      }
    }
    loadSuppliers()
  }, [strategicOnly, selectedSupplier, portalIsSupplier, viewKey])

  // Fetch scorecard data
  const fetchData = useCallback(async () => {
    // Supplier portal: hold until the customer resolves. Firing without
    // customer_id earns a 403 from scope_resolver, which is what the tab used
    // to render as "Failed to load performance data".
    if (portalIsSupplier && supplierCustomerId === undefined) {
      setLoading(true)
      return
    }
    // Resolved to nothing — the call can never be authorised, so stop rather
    // than spin on a request that will never be made.
    if (portalIsSupplier && supplierCustomerId === null) {
      setLoading(false)
      setError(null)
      setNoCustomer(true)
      setScorecard(null)
      return
    }
    setNoCustomer(false)
    try {
      setLoading(true)
      setError(null)

      const params: Record<string, number | string | boolean> = {}
      if (portalIsSupplier && supplierCustomerId) {
        params['customer_id'] = supplierCustomerId
      }
      if (!portalIsSupplier && appliedFilters.supplier !== 'all') {
        params['supplier_id'] = parseInt(appliedFilters.supplier, 10)
      }
      if (appliedFilters.startDate) {
        params['start_date'] = appliedFilters.startDate
      }
      if (appliedFilters.endDate) {
        params['end_date'] = appliedFilters.endDate
      }
      if (appliedFilters.strategicOnly) {
        params['strategic_only'] = true
      }

      const response = await performanceScorecardAPI.get(params)
      setScorecard(response.data)
    } catch (err: unknown) {
      const axiosError = err as { response?: { status?: number }; message?: string }
      console.error('[PerformanceTab] Error fetching scorecard:', axiosError.message)
      setError('Failed to load performance data. Please try again.')
      setScorecard(null)
    } finally {
      setLoading(false)
    }
  }, [appliedFilters, portalIsSupplier, supplierCustomerId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Handle search button click.
  // Takes an optional supplier override so picking one from the dropdown can
  // apply immediately - the setState from onSupplierChange has not landed yet
  // at that point, so reading selectedSupplier here would search the PREVIOUS
  // supplier.
  const handleSearch = useCallback((supplierOverride?: string) => {
    setAppliedFilters({
      supplier: supplierOverride ?? selectedSupplier,
      startDate,
      endDate,
      strategicOnly,
    })
  }, [selectedSupplier, startDate, endDate, strategicOnly])

  // Handle cancel/reset button click
  const handleCancel = useCallback(() => {
    const defaults = getDefaultDateRange()
    setSelectedSupplier('all')
    setStartDate(defaults.startDate)
    setEndDate(defaults.endDate)
    setStrategicOnly(false)
    setAppliedFilters({
      supplier: 'all',
      startDate: defaults.startDate,
      endDate: defaults.endDate,
      strategicOnly: false,
    })
  }, [])

  // Format OTIF percentage with N/A handling.
  // Precision comes from Admin Setup -> Regional -> Decimal Precision
  // (`kpi_percent_decimals`), the same setting the Scheduled Dashboard's OTIF
  // ring reads. This card used formatNumber, which is a 0-or-2 switch and
  // defaults to whole numbers — so a tenant configured for two decimals saw
  // 98.78% on one dashboard and 99% on this one for the same figure.
  const kpiDecimals = preferences.kpi_percent_decimals ?? 0
  const formatOTIF = useCallback(
    (value: number | null): string => {
      if (value === null || value === undefined) return 'N/A'
      return `${value.toFixed(kpiDecimals)}%`
    },
    [kpiDecimals]
  )

  // Export PDF function
  const handleExportPDF = useCallback(async () => {
    if (!scorecard || !scorecardRef.current) return

    setExporting(true)
    try {
      const month = scorecard.header.month || new Date().toLocaleString('en-US', { month: 'long' })
      const year = scorecard.header.year || new Date().getFullYear()
      const hasChart = showOTIF && scorecard.daily_otif && scorecard.daily_otif.length > 0

      await exportPerformancePDF({
        supplierName: scorecard.header.supplier_name || 'AllSuppliers',
        month,
        year,
        scorecardElement: scorecardRef.current,
        chartElement: hasChart ? chartRef.current : null,
      })
    } catch (err) {
      console.error('[PerformanceTab] Error exporting PDF:', err)
    } finally {
      setExporting(false)
    }
  }, [scorecard, showOTIF])

  // Memoized section data
  const sectionData = useMemo((): SectionDataMap | null => {
    if (!scorecard) return null

    return {
      line_orders: {
        ytd: `${formatNumber(scorecard.line_orders.delivered_ytd)} / ${formatNumber(scorecard.line_orders.expected_ytd)}`,
        month: `${formatNumber(scorecard.line_orders.delivered_month)} / ${formatNumber(scorecard.line_orders.expected_month)}`,
        ytdLabel: 'Lines Delivered/Expected YTD',
        monthLabel: 'Lines Delivered/Expected Month',
      },
      ...(showOTIF ? { delivery_performance: {
        ytd: formatOTIF(scorecard.delivery_performance.otif_ytd),
        month: formatOTIF(scorecard.delivery_performance.otif_month),
        ytdLabel: 'OTIF % YTD',
        monthLabel: 'OTIF % Month',
      } } : {}),
      cost: {
        ytd: formatCurrency(scorecard.cost.value_received_ytd, scorecard.cost.currency),
        month: formatCurrency(scorecard.cost.value_received_month, scorecard.cost.currency),
        ytdLabel: 'Value Received YTD',
        monthLabel: 'Value Received Month',
      },
      returns: {
        ytd: formatNumber(scorecard.returns.rtvs_ytd),
        month: formatNumber(scorecard.returns.rtvs_month),
        ytdLabel: 'Returns Raised YTD',
        monthLabel: 'Returns Raised Month',
      },
      quality: {
        ytd: formatNumber(scorecard.quality.concessions_accepted_ytd),
        month: formatNumber(scorecard.quality.concessions_raised_month),
        ytdLabel: 'Concessions Accepted YTD',
        monthLabel: 'Concessions Raised Month',
      },
      supplier_concerns: {
        ytd: formatNumber(scorecard.supplier_concerns.ncrs_ytd),
        month: formatNumber(scorecard.supplier_concerns.ncrs_month),
        ytdLabel: 'Concerns Raised YTD',
        monthLabel: 'Concerns Raised Month',
      },
    }
  }, [scorecard, formatNumber, formatCurrency, formatOTIF, showOTIF])

  // Memoized drilldown filters for clickable values
  const drilldownFilters = useMemo((): DrilldownFilters | undefined => {
    if (!scorecard) return undefined

    // Calculate YTD start date (January 1st of the scorecard year)
    const ytdStartDate = `${scorecard.header.year}-01-01`
    // YTD end date is the applied end date
    const ytdEndDate = appliedFilters.endDate

    return {
      supplierId: scorecard.header.supplier_id,
      supplierName: scorecard.header.supplier_name,
      ytdStartDate,
      ytdEndDate,
      monthStartDate: appliedFilters.startDate,
      monthEndDate: appliedFilters.endDate,
    }
  }, [scorecard, appliedFilters])

  return (
    <div className="space-y-4">
      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportPDF}
          disabled={exporting || !scorecard}
        >
          {exporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <FileDown className="h-4 w-4 mr-2" />
          )}
          {exporting ? 'Exporting...' : 'Export PDF'}
        </Button>
        <Button
          variant={showFilters ? 'default' : 'outline'}
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter className="h-4 w-4 mr-2" />
          Filter
        </Button>
      </div>

      {/* Filter Bar */}
      {showFilters && (
        <PerformanceFilterBar
          suppliers={suppliers}
          loadingSuppliers={loadingSuppliers}
          selectedSupplier={selectedSupplier}
          onSupplierChange={setSelectedSupplier}
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          strategicOnly={strategicOnly}
          onStrategicOnlyChange={setStrategicOnly}
          onSearch={handleSearch}
          onCancel={handleCancel}
          isSupplier={portalIsSupplier}
          customers={customers}
          selectedCustomer={supplierCustomerId ? String(supplierCustomerId) : ''}
          onCustomerChange={(v) => setSupplierCustomerId(parseInt(v, 10))}
          loadingCustomers={loadingCustomers}
        />
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* No customer to report against — supplier portal only. Not an error:
          the supplier simply has no active trading relationship yet. */}
      {!loading && noCustomer && (
        <Card className="shadow-sm">
          <CardContent className="flex flex-col items-center justify-center gap-3 py-14 text-center">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-muted">
              <BarChart3 className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">No performance data yet</p>
            <p className="max-w-sm text-sm text-muted-foreground">
              Your scorecard appears here once a customer you trade with starts
              raising orders against your company.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {!loading && error && (
        <Card className="shadow-sm">
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <div className="text-red-500 mb-4">{error}</div>
              <Button variant="outline" onClick={fetchData}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Data State */}
      {!loading && !error && !noCustomer && (!scorecard || !sectionData) && (
        <Card className="shadow-sm">
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Performance Data</h3>
              <p className="text-sm text-muted-foreground">
                Performance metrics will appear here once data is available.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scorecard Content */}
      {!loading && !error && scorecard && sectionData && (
        <>
          {/* PDF Capture Area - Page 1: Scorecard */}
          <div ref={scorecardRef} className="bg-white py-4">
            {/* Header: title + context chips, one line instead of a title row and a two-row table */}
            <div className="flex flex-wrap items-end justify-between gap-3 mb-4 pb-3 border-b">
              <div>
                <h2 className="text-xl font-bold">Supplier Performance</h2>
                <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5">
                    <span className="text-xs uppercase tracking-wide">Supplier</span>
                    <span className="font-medium text-foreground">{scorecard.header.supplier_name}</span>
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5">
                    <span className="text-xs uppercase tracking-wide">Period</span>
                    <span className="font-medium text-foreground">{scorecard.header.month} {scorecard.header.year}</span>
                  </span>
                </div>
              </div>
              <div className="text-xs text-muted-foreground text-right">
                Last data update <span className="font-medium text-foreground">{scorecard.header.report_date}</span>
              </div>
            </div>

            {/* Scorecard Table */}
            <PerformanceScorecardTable
              sectionData={sectionData}
              supplierName={scorecard.header.supplier_name}
              drilldownFilters={drilldownFilters}
            />

            {/* Supplier Rating Cards */}
            <PerformanceRatingCards
              // Customer review 11 Sep (msg 2): the configured label + palette
              // colour when the API sends them; the letter + colour name otherwise.
              ratingYtd={scorecard.supplier_rating.rating_ytd_label || scorecard.supplier_rating.rating_ytd}
              ratingYtdColor={scorecard.supplier_rating.rating_ytd_color}
              ratingYtdHex={scorecard.supplier_rating.rating_ytd_hex}
              statementYtd={scorecard.supplier_rating.statement_ytd}
              ratingMonth={scorecard.supplier_rating.rating_month_label || scorecard.supplier_rating.rating_month}
              ratingMonthColor={scorecard.supplier_rating.rating_month_color}
              ratingMonthHex={scorecard.supplier_rating.rating_month_hex}
              statementMonth={scorecard.supplier_rating.statement_month}
              monthLabel={scorecard.header.month}
            />

            {/* Change Requests Charts */}
            {scorecard.change_requests && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <PerformanceChangeRequestsCharts
                  changeRequests={scorecard.change_requests}
                  period="ytd"
                  periodLabel="Year to Date"
                  drilldownFilters={drilldownFilters ? {
                    supplierId: drilldownFilters.supplierId,
                    startDate: drilldownFilters.ytdStartDate,
                    endDate: drilldownFilters.ytdEndDate,
                  } : undefined}
                />
                <PerformanceChangeRequestsCharts
                  changeRequests={scorecard.change_requests}
                  period="month"
                  periodLabel={scorecard.header.month}
                  drilldownFilters={drilldownFilters ? {
                    supplierId: drilldownFilters.supplierId,
                    startDate: drilldownFilters.monthStartDate,
                    endDate: drilldownFilters.monthEndDate,
                  } : undefined}
                />
              </div>
            )}
          </div>

          {/* PDF Capture Area - Page 2: Daily OTIF Chart */}
          {showOTIF && scorecard.daily_otif && scorecard.daily_otif.length > 0 && (
            <PerformanceDailyOTIFChart ref={chartRef} data={scorecard.daily_otif} />
          )}
        </>
      )}
    </div>
  )
}
